import { format } from 'date-fns'
import { useEffect, useState } from 'react'

import { Button } from '@/components/button/button'
import SvgIcon from '@/components/svg-icon/svgIcon'
import { Typography } from '@/components/typography/typography'
import { cn } from '@/libs/tw'
import { useCartStore } from '@/stores/cart-store/cartStore'
import { CartRoom } from '@/stores/cart-store/slices/types'
import { formatCurrency } from '@/utils/formatCurrency'
import { translate } from '@/utils/translator'

import { useRoomContext } from '../hooks/useRoomContext'
import { useOffers } from '../hooks/useRoomOffers'
import { RoomOffer } from '../types'
import { getServicePrice } from '../utils/getServicePrice'

type RoomSummaryProps = {
	room: CartRoom
	hasEditMode?: boolean
}

export const RoomSummary = ({ room, hasEditMode }: RoomSummaryProps) => {
	const { setIsEditMode, roomConfig, roomNumber } = useRoomContext()
	const [offer, setOffer] = useState<RoomOffer>()

	const { selectedRoomCodes } = useCartStore.use.roomSelection()
	const removeRoom = useCartStore.use.removeRoom()

	const otherSelectedRoomCodes = selectedRoomCodes
		.filter((code) => code.roomConfigId !== roomConfig.id)
		.map((code) => code.roomCode)

	const { data: offers } = useOffers({
		adults: roomConfig.adults,
		childrenAges: roomConfig.children,
		selectedRoomCodes: otherSelectedRoomCodes,
	})

	const getTotalPrice = () => {
		const totalServiceCost = room.services.reduce((prev, curr) => {
			if (!curr.price?.amountAfterTax) {
				return prev + 0
			}

			return prev + curr.price.amountAfterTax * (curr.quantity ?? 1)
		}, 0)

		return room.rate.price.amountAfterTax + totalServiceCost
	}

	useEffect(() => {
		if (!offers?.roomTypeOffers) return

		const isRoomAvailable = offers?.roomTypeOffers.find(
			(currentRoom) =>
				currentRoom.roomTypeCode === room.roomTypeCode &&
				currentRoom.rates.find(
					(currentRate) => currentRate.ratePlanCode === room.rate.ratePlanCode
				)
		)

		setOffer(isRoomAvailable)
		if (!isRoomAvailable) {
			removeRoom(roomConfig.id)
		}
	}, [removeRoom, room.roomTypeCode, room.rate.ratePlanCode, roomConfig.id, offers])

	return (
		<div
			className={cn(
				'border-t border-textColor pt-24 text-textColor md:rounded-8 md:border-none md:bg-naturel md:p-24 md:pb-32 md:text-roomSelection-summaryTextColor',
				hasEditMode
					? 'md:text-roomSelection-textColor'
					: 'md:bg-roomSelection-summaryBgColor'
			)}
		>
			<div className={cn('flex gap-16 ')}>
				<img
					src={room.image?.src ?? offer?.images[0].largeUrl}
					alt={room.image?.alt ?? offer?.images[0].alt}
					title={room.image?.title ?? offer?.images[0].title}
					className={cn('h-[80px] w-[80px] shrink-0 basis-[80px] rounded-8 md:hidden')}
				/>
				<div className={cn('flex w-full flex-col gap-16 md:gap-24')}>
					<Typography element="p" style="p-bold" className={cn('md:hidden')}>
						{`${translate('room-selection-room')} ${roomNumber}`}
					</Typography>
					<div className={cn('hidden justify-between gap-8 md:flex')}>
						<Typography element="h3" style="h3">
							{room.title}
						</Typography>
						<SvgIcon collectionName="ibe3" iconName="check" className="h-24 w-24" />
					</div>
					{room.rate.cancellationDeadline && (
						<Typography style="p" element="p" className={cn('w-2/3')}>
							{translate('room-selection-cancel-till')}{' '}
							{format(new Date(room.rate.cancellationDeadline), 'dd.MM.yyy / HH:mm')}
						</Typography>
					)}
					<div className={cn('mb-8 flex justify-between md:hidden')}>
						<Typography element="h3" style="p-bold">
							{room.title}
						</Typography>
						<Typography style="p-bold" element="p">
							{formatCurrency(room.rate.price.amountAfterTax, true)}
						</Typography>
					</div>
					<div className={cn('hidden justify-between gap-40 md:flex')}>
						<Typography style="p-m" element="p" className="hyphens-auto ">
							{room.rate.title}
						</Typography>
						<Typography style="p-m" element="p">
							{formatCurrency(room.rate.price.amountAfterTax, true)}
						</Typography>
					</div>
					{room.services.length > 0 && (
						<div className={cn('flex flex-col gap-16')}>
							<Typography style="p-bold" element="p">
								{translate('room-selection-additions')}
							</Typography>
							<div
								className={cn(
									'flex flex-col gap-16 divide-dark-light md:gap-0 md:divide-y-[1px]'
								)}
							>
								{room.services.map((service) => (
									<div
										key={service.serviceCode}
										className={cn('flex justify-between gap-40 md:py-16')}
									>
										<Typography style="p" element="p">
											{service.title}
										</Typography>
										<Typography style="p" element="p">
											{formatCurrency(
												getServicePrice(
													service.price?.amountAfterTax,
													service.quantity
												)
											)}
										</Typography>
									</div>
								))}
							</div>
						</div>
					)}
					<div className={cn('pb-23 flex justify-between gap-40 pt-8')}>
						<Typography style="p-m-bold" element="p">
							{translate('room-selection-sum')}
						</Typography>
						<Typography style="p-m-bold" element="p">
							{formatCurrency(getTotalPrice(), true)}
						</Typography>
					</div>
				</div>
			</div>
			{hasEditMode && (
				<>
					<Button
						style="primary"
						onClick={() => setIsEditMode(true)}
						className={cn(
							'border-button-textColor mt-32 w-full border text-textColor md:hidden md:border-primary'
						)}
					>
						{translate('room-selection-edit-room')}
					</Button>
					<Button
						style="naturel"
						onClick={() => setIsEditMode(true)}
						className={cn('mt-32 hidden w-full border border-button-text md:block')}
					>
						{translate('room-selection-edit-room')}
					</Button>
				</>
			)}
		</div>
	)
}
